import { Chip, Dropdown, Header, Label, Separator } from "@heroui/react";
import { UserRoleEnum } from "@open-booking/shared";
import {
	CalendarCheck,
	CalendarDays,
	ChartBar,
	FileText,
	House,
	LayoutDashboard,
	LogOut,
	Menu,
	Monitor,
	Moon,
	Settings,
	ShieldCheck,
	Sun,
	Users,
	X,
} from "lucide-react";
import { memo, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, Outlet, useLocation } from "react-router";
import { AUTH_ROUTES } from "@/src/auth/constants/auth-routes";
import { useLogout } from "@/src/auth/hooks/use-logout";
import { getIsAuthenticated, getUser } from "@/src/auth/store/auth-selectors";
import logo from "@/src/common/assets/images/logo.png";
import { RouteFallback } from "@/src/common/components/route-fallback";
import { env } from "@/src/common/constants/env";
import { useAppDispatch, useAppSelector } from "@/src/common/redux/store";
import { getColorScheme } from "@/src/common/store/general-selectors";
import { type ColorScheme, setColorScheme } from "@/src/common/store/general-slice";
import { getInitials } from "@/src/common/utils/get-initials";
import { OWNER_ROUTES } from "@/src/owner/constants/owner-routes";
import { useTeamPermission } from "@/src/owner/hooks/use-team-permissions";
import { useGetCautionsBadgeCountQuery } from "@/src/owner/services/caution-http.service";

const SidebarNavItem = memo(function SidebarNavItem({
	to,
	icon: Icon,
	label,
	end,
	onNavigate,
	badgeCount,
}: {
	to: string;
	icon: React.ElementType;
	label: string;
	end?: boolean;
	onNavigate?: () => void;
	badgeCount?: number;
}) {
	const { pathname } = useLocation();
	const isActive = end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);

	return (
		<Link
			to={to}
			onClick={onNavigate}
			className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
				isActive
					? "bg-accent-soft text-accent font-semibold"
					: "font-medium text-foreground/70 hover:bg-surface-hover hover:text-foreground"
			}`}
		>
			<Icon className="size-[18px]" />
			<span className="flex-1">{label}</span>
			{typeof badgeCount === "number" && badgeCount > 0 && (
				<Chip color="danger" variant="primary" size="sm" className="rounded-full">
					{badgeCount}
				</Chip>
			)}
		</Link>
	);
});

function DashboardSidebar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
	const { t } = useTranslation("owner");
	const user = useAppSelector(getUser);
	const colorScheme = useAppSelector(getColorScheme);
	const dispatch = useAppDispatch();
	const logout = useLogout();
	const { data: cautionBadge } = useGetCautionsBadgeCountQuery(undefined, { pollingInterval: 5 * 60 * 1000 });
	const canManageTenants = useTeamPermission("tenants_manage");

	const handleThemeChange = (keys: "all" | Set<string | number>) => {
		if (keys === "all") return;
		const next = Array.from(keys)[0];
		if (next === "light" || next === "dark" || next === "system") {
			dispatch(setColorScheme(next satisfies ColorScheme));
		}
	};

	return (
		<>
			{isOpen && (
				<button
					type="button"
					className="fixed inset-0 z-30 bg-black/50 md:hidden"
					onClick={onClose}
					aria-label={t("layout.close_sidebar")}
				/>
			)}

			<aside
				className={`fixed inset-y-0 left-0 z-40 flex w-[260px] shrink-0 flex-col border-r border-border bg-surface p-4 transition-transform md:static md:z-auto md:translate-x-0 ${
					isOpen ? "translate-x-0" : "-translate-x-full"
				}`}
			>
				<div className="flex items-center justify-between pb-4 pt-4">
					<div className="flex items-center gap-2.5">
						<img src={logo} alt={env.APP_NAME} className="size-6 hidden md:block" />
						<span className="text-lg font-bold">{env.APP_NAME}</span>
					</div>
					<button type="button" className="rounded-lg p-1 hover:bg-surface-hover md:hidden" onClick={onClose}>
						<X className="size-5 text-muted" />
					</button>
				</div>

				<div className="pb-2 pt-8">
					<span className="text-xs font-semibold uppercase text-muted">{t("sidebar.general")}</span>
				</div>
				<nav className="flex flex-col gap-0.5">
					<SidebarNavItem to={OWNER_ROUTES.DASHBOARD} icon={LayoutDashboard} label={t("sidebar.dashboard")} end onNavigate={onClose} />
					<SidebarNavItem to={OWNER_ROUTES.PROPERTIES} icon={House} label={t("sidebar.properties")} onNavigate={onClose} />
					<SidebarNavItem to={OWNER_ROUTES.BOOKINGS} icon={CalendarDays} label={t("sidebar.bookings")} onNavigate={onClose} />
					<SidebarNavItem to={OWNER_ROUTES.CALENDAR} icon={CalendarCheck} label={t("sidebar.calendar")} onNavigate={onClose} />
					{canManageTenants && <SidebarNavItem to={OWNER_ROUTES.TENANTS} icon={Users} label={t("sidebar.tenants")} onNavigate={onClose} />}
					<SidebarNavItem
						to={OWNER_ROUTES.CAUTIONS}
						icon={ShieldCheck}
						label={t("sidebar.cautions")}
						badgeCount={cautionBadge?.count}
						onNavigate={onClose}
					/>
					<SidebarNavItem to={OWNER_ROUTES.TEMPLATES} icon={FileText} label={t("sidebar.templates")} onNavigate={onClose} />
					<SidebarNavItem to={OWNER_ROUTES.REVENUE} icon={ChartBar} label={t("sidebar.revenue")} onNavigate={onClose} />
				</nav>

				<div className="pb-2 pt-4">
					<span className="text-xs font-semibold uppercase text-muted">{t("sidebar.settings_section")}</span>
				</div>
				<nav className="flex flex-col gap-0.5">
					<SidebarNavItem to={OWNER_ROUTES.SETTINGS} icon={Settings} label={t("sidebar.settings")} onNavigate={onClose} />
				</nav>

				<div className="flex-1" />

				{user && (
					<div className="pt-3">
						<Dropdown>
							<Dropdown.Trigger className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left transition-colors hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
								<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent/10 text-xs font-semibold text-accent">
									{getInitials(user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : null, user.email)}
								</div>
								<div className="flex min-w-0 flex-col">
									<span className="truncate text-sm font-medium">
										{user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
									</span>
									<span className="truncate text-xs text-muted">{user.email}</span>
								</div>
							</Dropdown.Trigger>
							<Dropdown.Popover placement="top" className="min-w-[220px]">
								<Dropdown.Menu onAction={(key) => key === "logout" && logout()}>
									<Dropdown.Section selectionMode="single" selectedKeys={new Set([colorScheme])} onSelectionChange={handleThemeChange}>
										<Header>{t("sidebar.theme.label")}</Header>
										<Dropdown.Item id="light" textValue={t("sidebar.theme.light")}>
											<Sun className="size-4 shrink-0 text-muted" />
											<Label>{t("sidebar.theme.light")}</Label>
											<Dropdown.ItemIndicator />
										</Dropdown.Item>
										<Dropdown.Item id="system" textValue={t("sidebar.theme.system")}>
											<Monitor className="size-4 shrink-0 text-muted" />
											<Label>{t("sidebar.theme.system")}</Label>
											<Dropdown.ItemIndicator />
										</Dropdown.Item>
										<Dropdown.Item id="dark" textValue={t("sidebar.theme.dark")}>
											<Moon className="size-4 shrink-0 text-muted" />
											<Label>{t("sidebar.theme.dark")}</Label>
											<Dropdown.ItemIndicator />
										</Dropdown.Item>
									</Dropdown.Section>
									<Separator />
									<Dropdown.Item id="logout" textValue={t("sidebar.logout")} variant="danger">
										<div className="flex items-center gap-2">
											<LogOut className="size-4" />
											<Label>{t("sidebar.logout")}</Label>
										</div>
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown.Popover>
						</Dropdown>
					</div>
				)}
			</aside>
		</>
	);
}

export function OwnerRouteLayout() {
	const isAuthenticated = useAppSelector(getIsAuthenticated);
	const user = useAppSelector(getUser);
	const location = useLocation();
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);

	if (!isAuthenticated) {
		const returnTo = `${location.pathname}${location.search}`;
		const authUrl = `${AUTH_ROUTES.AUTH}?redirect=${encodeURIComponent(returnTo)}`;
		return <Navigate to={authUrl} replace />;
	}

	if (user?.role !== UserRoleEnum.OWNER) {
		return <Navigate to="/" replace />;
	}

	return (
		<div className="flex h-screen bg-background">
			<div className="fixed top-0 left-0 right-0 z-20 flex items-center gap-3 border-b border-border bg-surface px-4 py-3 md:hidden">
				<button type="button" className="rounded-lg p-1 hover:bg-surface-hover" onClick={() => setIsSidebarOpen(true)}>
					<Menu className="size-5 text-muted" />
				</button>
				<div className="flex items-center gap-2">
					<img src={logo} alt={env.APP_NAME} className="size-6 hidden md:block" />
					<span className="text-sm font-bold">{env.APP_NAME}</span>
				</div>
			</div>

			<DashboardSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
			<main className="flex-1 overflow-auto pt-14 md:pt-0 bg-background">
				<Suspense fallback={<RouteFallback />}>
					<Outlet />
				</Suspense>
			</main>
		</div>
	);
}
